/**
 * PSR Phase 2 — tier-1 sentiment fetcher contracts.
 *
 * Each fetcher returns structured facts + citation refs; never throws.
 */

export type SentimentFetcherSourceId =
  | 'census-acs'
  | 'bls'
  | 'fcc'
  | 'local-public-data'
  | 'local-gis'
  | 'news-aggregator';

export type SentimentCoverage = 'full' | 'partial' | 'none';

export interface SentimentNormalizedFact {
  key: string;
  label: string;
  value: string | number;
  units?: string;
  url?: string;
  observedAt: string;
}

/** Citation anchor for G1 / citation-support verifier. */
export interface CitationRef {
  index: number;
  label: string;
  url: string;
  sourceId: SentimentFetcherSourceId;
  factKey: string;
}

export interface SentimentFetchResult {
  sourceId: SentimentFetcherSourceId;
  coverage: SentimentCoverage;
  normalizedFacts: SentimentNormalizedFact[];
  citationRefs: CitationRef[];
  errors: string[];
  fetchedAt: string;
  cached?: boolean;
}

export interface SentimentFetchOptions {
  /** Attempt-owned report cancellation/watchdog signal. */
  signal?: AbortSignal;
  trendWindowDays?: number;
  perSourceCap?: number;
  mode?: 'feasibility' | 'standalone-psr';
  skipCache?: boolean;
  /** Admin callers supply all available context and never fall back to client auth. */
  explicitContext?: boolean;
  queryFingerprint?: string;
  technologyTerms?: string[];
  /** Pre-resolved geo — avoids duplicate Firestore reads when orchestrator already has SED. */
  lat?: number;
  lng?: number;
  state?: string;
  county?: string;
  locality?: string;
  fipsState?: string;
  fipsCounty?: string;
  fipsTract?: string;
}

export interface SentimentFetcher {
  sourceId: SentimentFetcherSourceId;
  /** R-130-10 — site-independent health probe for Admin Sentiment Sources. */
  probeAvailability: () => Promise<FetcherAvailability>;
  isAvailable: (siteId: string, opts?: SentimentFetchOptions) => Promise<boolean>;
  fetch: (siteId: string, opts?: SentimentFetchOptions) => Promise<SentimentFetchResult>;
}

/** R-130-10 — health-only probe payload for upstream status chips. */
export interface FetcherAvailability {
  /** R-130-10 — final health state rendered by the admin UI chip.
   *  'rate-limited' = upstream responded HTTP 429: up, but throttled —
   *  distinct from 'down' so quota pressure doesn't read as an outage. */
  state: 'ok' | 'down' | 'unconfigured' | 'rate-limited' | 'unknown';
  /** R-130-10 — round-trip latency in ms for successful network probes. */
  latencyMs?: number;
  /** R-130-10 — short reason for non-ok states (status, timeout, missing env). */
  reason?: string;
  /** R-130-10 — epoch milliseconds when the probe completed. */
  probedAt: number;
}

export const TIER1_SENTIMENT_FETCHER_IDS: readonly SentimentFetcherSourceId[] = [
  'census-acs',
  'bls',
  'fcc',
  'local-public-data',
  'local-gis',
  'news-aggregator',
] as const;

export function emptySentimentResult(
  sourceId: SentimentFetcherSourceId,
  errors: string[],
): SentimentFetchResult {
  return {
    sourceId,
    coverage: 'none',
    normalizedFacts: [],
    citationRefs: [],
    errors,
    fetchedAt: new Date().toISOString(),
  };
}
